// FinKloz — Bank Rec Discrepancies & Proposed JEs
// Shows reconciliation summary, reconciling items, and proposed journal entries.

import React from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, useTheme } from '../tokens';

const teal = '#0D9488';
const tealLight = '#CCFBF1';

function fmt$(n) {
  if (n === null || n === undefined) return '--';
  if (n === 0) return '--';
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `$(${str})` : `$${str}`;
}

function RecLine({ label, value, bold, highlight, t }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '4px 0',
      borderBottom: bold ? `2px solid ${t.border}` : `1px solid ${t.borderLight}`,
    }}>
      <span style={{
        fontFamily: sans, fontSize: 12,
        fontWeight: bold ? 700 : 400,
        color: t.textPrimary,
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: mono, fontSize: 12,
        fontWeight: bold ? 700 : 400,
        color: highlight === 'green' ? green : highlight === 'red' ? red : t.textPrimary,
      }}>
        {fmt$(value)}
      </span>
    </div>
  );
}

function TypeBadge({ type, t }) {
  const labels = {
    bank_fee: 'Bank Fee',
    interest: 'Interest',
    nsf: 'NSF',
    outstanding_check: 'Outstanding',
    deposit_in_transit: 'In Transit',
    unmatched: 'Unmatched',
  };
  const colors = {
    bank_fee: { bg: amberLight, fg: amber },
    interest: { bg: greenLight, fg: green },
    nsf: { bg: redLight, fg: red },
    outstanding_check: { bg: '#EDE9FE', fg: '#7C3AED' },
    deposit_in_transit: { bg: tealLight, fg: teal },
    unmatched: { bg: redLight, fg: red },
  };
  const c = colors[type] || { bg: t.pillBg, fg: t.textSecondary };
  return (
    <span style={{
      fontFamily: sans, fontSize: 10, fontWeight: 600,
      padding: '2px 6px', borderRadius: 3,
      background: c.bg, color: c.fg,
    }}>
      {labels[type] || type}
    </span>
  );
}

export default function BankRecDiscrepancies({
  reconciliation,
  reconcilingItems,
  proposedJEs,
  onApproveJE,
  onApproveAllJEs,
  currency,
}) {
  const t = useTheme();

  if (!reconciliation) return null;

  const isUSD = currency === 'USD';

  return (
    <div>
      {/* Reconciliation Summary */}
      <div style={{
        background: t.bgSurface, borderRadius: 8, padding: 14,
        border: `1px solid ${t.border}`, marginBottom: 12,
      }}>
        <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>
          Reconciliation
        </div>

        <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>
          Bank Side
        </div>
        <RecLine label="Bank Statement Balance" value={reconciliation.bankBalance} t={t} />
        <RecLine label="Less: Outstanding Checks" value={-reconciliation.outstandingChecks} t={t} />
        <RecLine label="Plus: Deposits in Transit" value={reconciliation.depositsInTransit} t={t} />
        <RecLine label="Adjusted Bank Balance" value={reconciliation.adjustedBankBalance} bold t={t} />

        <div style={{ height: 12 }} />

        <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>
          Book Side
        </div>
        <RecLine label="GL Balance (per books)" value={reconciliation.glBalance} t={t} />
        {reconciliation.bankFees !== 0 && <RecLine label="Bank Fees (to book)" value={reconciliation.bankFees} t={t} />}
        {reconciliation.interest !== 0 && <RecLine label="Interest (to book)" value={reconciliation.interest} t={t} />}
        {reconciliation.otherAdjustments !== 0 && <RecLine label="Other Adjustments" value={reconciliation.otherAdjustments} t={t} />}
        <RecLine label="Adjusted Book Balance" value={reconciliation.adjustedBookBalance} bold t={t} />

        <div style={{ height: 8 }} />
        <RecLine
          label="DIFFERENCE"
          value={reconciliation.difference}
          bold
          highlight={reconciliation.isReconciled ? 'green' : 'red'}
          t={t}
        />

        {/* Status badge */}
        <div style={{
          marginTop: 8, textAlign: 'center', padding: '6px 0',
          background: reconciliation.isReconciled ? greenLight : redLight,
          borderRadius: 4,
          fontFamily: sans, fontSize: 12, fontWeight: 600,
          color: reconciliation.isReconciled ? greenDark : red,
        }}>
          {reconciliation.isReconciled ? 'RECONCILED' : `UNRECONCILED -- Diff: ${fmt$(reconciliation.difference)}`}
        </div>
      </div>

      {/* Reconciling Items — only show items that need JEs (bank fees, interest, NSF), not timing items */}
      {(() => {
        const actionableItems = reconcilingItems.filter(i => i.action === 'book_je');
        if (actionableItems.length === 0) return null;
        return (
          <div style={{
            background: t.bgSurface, borderRadius: 8, padding: 14,
            border: `1px solid ${t.border}`, marginBottom: 12,
          }}>
            <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>
              Items Requiring Journal Entry ({actionableItems.length})
            </div>

            {actionableItems.map((item, i) => (
              <div key={item.id || i} style={{
                display: 'grid', gridTemplateColumns: '70px 1fr 80px',
                gap: 6, padding: '5px 0', alignItems: 'center',
                borderBottom: `1px solid ${t.borderLight}`,
              }}>
                <TypeBadge type={item.type} t={t} />
                <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.description}
                </span>
                <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, textAlign: 'right' }}>
                  {fmt$(item.amount)}
                </span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Proposed Journal Entries */}
      {proposedJEs && proposedJEs.length > 0 && (
        <div style={{
          background: t.bgSurface, borderRadius: 8, padding: 14,
          border: `1px solid ${t.border}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: t.textPrimary }}>
              Proposed Journal Entries ({proposedJEs.length})
            </div>
            {onApproveAllJEs && (
              <button
                onClick={onApproveAllJEs}
                style={{
                  fontFamily: sans, fontSize: 11, fontWeight: 600,
                  padding: '4px 10px', borderRadius: 4,
                  border: `1px solid ${teal}`, background: tealLight, color: teal,
                  cursor: 'pointer',
                }}
              >
                Approve All
              </button>
            )}
          </div>

          {/* Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 60px 60px 80px 30px',
            gap: 6, padding: '4px 0',
            fontFamily: sans, fontSize: 10, fontWeight: 600, color: t.textMuted,
            borderBottom: `1px solid ${t.border}`,
          }}>
            <span>Description</span>
            <span>Debit</span>
            <span>Credit</span>
            <span style={{ textAlign: 'right' }}>Amount</span>
            <span></span>
          </div>

          {proposedJEs.map((je, i) => (
            <div key={je.id || i} style={{
              display: 'grid', gridTemplateColumns: '1fr 60px 60px 80px 30px',
              gap: 6, padding: '5px 0', alignItems: 'center',
              borderBottom: `1px solid ${t.borderLight}`,
              opacity: je.approved ? 0.7 : 1,
            }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {je.description}
              </span>
              <span style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary }}>{je.debitAccount}</span>
              <span style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary }}>{je.creditAccount}</span>
              <span style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, textAlign: 'right' }}>
                {fmt$(je.amount)}
              </span>
              <div
                onClick={() => onApproveJE && onApproveJE(je.id, !je.approved)}
                style={{
                  width: 18, height: 18, borderRadius: 4, cursor: 'pointer',
                  background: je.approved ? teal : t.bg,
                  border: `1px solid ${je.approved ? teal : t.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {je.approved && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>{'\u2713'}</span>}
              </div>
            </div>
          ))}

          {/* Post to Sage button (disabled until backend) */}
          <div style={{ marginTop: 10 }}>
            <button
              disabled
              title="Available when connected to Sage Intacct"
              style={{
                fontFamily: sans, fontSize: 12, fontWeight: 600,
                padding: '8px 16px', borderRadius: 6, width: '100%',
                border: `1px solid ${t.border}`, background: t.bgSurface,
                color: t.textMuted, cursor: 'not-allowed',
              }}
            >
              Post to Sage (coming soon)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
